import {
    get as _get,
    isEqual as _isEqual,
} from 'lodash';
import {withRouter} from 'react-router';

import {refetchQueryByName} from '@utils/apollo';
import DefaultForm from '@appComponents/DefaultForm';
import {onSubmit, renderModalError} from '@utils/forms';
import {showModal} from '@utils/modal';
import {routes} from '@constants/routes';
import {convertToInt, isUrlParamValid} from '@utils/helpers';

export class DistributionInvoiceStatusForm extends DefaultForm {
    componentDidMount() {
        if (!isUrlParamValid(this.props.formParams.optionsVariables.event)) {
            return showModal({
                isVisible: true,
                content: renderModalError('Event', routes.events.index.path),
            });
        }

        this.setCreateSuccessCallback(() => {
            refetchQueryByName('GetDistributionInvoiceStatusesForForm');
        });
        this.setUpdateSuccessCallback(() => {
            refetchQueryByName('GetDistributionInvoiceStatusesForForm');
        });
    }

    componentDidUpdate(prevProps) {
        const formPayload = _get(this.props, 'GraphQLOptionsData.distributionInvoiceStatusForm', null);
        let groups = this.state.groups;

        if (formPayload && !_isEqual(formPayload, this.state.formPayload)) {
            groups = this.withRowIndexes(_get(formPayload, 'groups', []) || []);
            const reportableByIndex = {};

            groups.forEach((group) => {
                group.rows.forEach((row) => {
                    reportableByIndex[row.index] = Boolean(row.is_reportable);
                });
            });

            const invoiceStatusValues = this.setDefaultInvoiceStatusValues(groups);

            this.setField('invoice_statuses', {
                groups,
                invoiceStatusValues,
                showWarning: Boolean(formPayload.show_warning),
                isEmpty: Boolean(formPayload.is_empty),
                eventId: this.props.formParams.optionsVariables.event,
                createEntity: this.props.CreateEntity,
                updateEntity: this.props.UpdateEntity,
                entityLabel: this.props.Model.entityLabel,
                messageBoxName: this.props.Model.messages.boxName,
            });
            this.setState(() => ({
                formPayload,
                groups,
                invoiceStatusValues,
                reportableByIndex,
                isEmpty: Boolean(formPayload.is_empty),
            }));
        }

        const autoUpdateValues = (_get(this.props, 'formValues.invoice_statuses', []) || [])
            .map((row) => _get(row, 'auto_update', undefined));

        if (!_isEqual(autoUpdateValues, this.state.autoUpdateValues)) {
            const invoiceStatusValues = this.onAutoUpdateChange(
                this.state.autoUpdateValues,
                autoUpdateValues,
                groups
            );
            const timestamps = invoiceStatusValues
                ? this.setTimestamps(
                    this.state.autoUpdateValues,
                    autoUpdateValues,
                    this.state.timestamps
                )
                : null;
            this.setField('invoice_statuses', {
                autoUpdateValues,
                ...(invoiceStatusValues ? {invoiceStatusValues, timestamps} : {}),
            });
            this.setState(() => ({
                autoUpdateValues,
                ...(invoiceStatusValues ? {invoiceStatusValues, timestamps} : {}),
            }));
        }

        super.componentDidUpdate(prevProps);
    }

    setDefaultInvoiceStatusValues = (groups) => {
        const invoiceStatusValues = [];

        groups.forEach((group) => {
            (group.rows || []).forEach((row) => {
                const saved = _get(row, 'distribution.invoice_status.invoice_status.id', '');
                const disRow = _get(row, 'distribution.invoice_status', null);
                const autoUpdateOn = disRow ? Boolean(disRow.auto_update) : true;

                if (saved) {
                    invoiceStatusValues[row.index] = saved;
                } else if (autoUpdateOn) {
                    invoiceStatusValues[row.index] = row.invoice_status ?? '';
                } else {
                    invoiceStatusValues[row.index] = '';
                }
            });
        });

        return invoiceStatusValues;
    };

    onAutoUpdateChange = (previousAutoUpdateValues, autoUpdateValues, groups) => {
        if (!previousAutoUpdateValues || !groups) {
            return null;
        }

        let changed = false;
        const invoiceStatusValues = [...(this.state.invoiceStatusValues || [])];

        groups.forEach((group) => {
            (group.rows || []).forEach((row) => {
                if (
                    !row.is_reportable
                    || false !== previousAutoUpdateValues[row.index]
                    || true !== autoUpdateValues[row.index]
                ) {
                    return;
                }

                invoiceStatusValues[row.index] = row.invoice_status ?? '';
                changed = true;
            });
        });

        return changed ? invoiceStatusValues : null;
    };

    setTimestamps = (previousAutoUpdateValues, autoUpdateValues, previousTimestamps) => {
        const timestamps = [...(previousTimestamps || [])];

        (autoUpdateValues || []).forEach((next, index) => {
            if (true === next && false === (previousAutoUpdateValues || [])[index]) {
                timestamps[index] = Date.now();
            }
        });

        return timestamps;
    };

    withRowIndexes = (groups) => {
        let index = 0;

        return groups.map((group) => ({
            ...group,
            rows: (group.rows || []).map((row) => ({
                ...row,
                index: index++,
            })),
        }));
    };

    renderSaveButton(props) {
        if (!this.state.groups || this.state.isEmpty) {
            return null;
        }

        return super.renderSaveButton({content: 'Save', ...props});
    }

    renderCancelButton() {
        return null;
    }

    onFormSubmit(data = {}) {
        const dataToSave = this.prepareDataForSubmit(data);

        this.setState(() => ({
            defaultForm_errorShowed: false,
        }));

        const callbacks = this.getCallbackOptions();

        return onSubmit({
            dataToSave,
            actions: {
                create: {
                    mutation: ({variables, ...options}) => {
                        const rows = variables.invoice_statuses || [];
                        const createRows = rows.filter((row) => !row.id);
                        const updateRows = rows.filter((row) => row.id);
                        const event = variables.event;
                        const requests = [];

                        if (createRows.length) {
                            requests.push(this.props.CreateEntity({
                                variables: {event, invoice_statuses: createRows},
                                ...options,
                            }));
                        }

                        if (updateRows.length) {
                            requests.push(this.props.UpdateEntity({
                                variables: {event, invoice_statuses: updateRows},
                                ...options,
                            }));
                        }

                        return requests.length ? Promise.all(requests) : Promise.resolve({data: {}});
                    },
                    options: _get(this.props, 'Model.mutationOptions.create', {}),
                },
                update: {
                    mutation: this.props.UpdateEntity,
                    options: _get(this.props, 'Model.mutationOptions.update', {}),
                },
            },
            message: super.getSubmitMessage(dataToSave),
            callback: super.getSubmitCallback(callbacks),
            apolloClient: this.props.client,
            props: this.props,
        });
    }

    prepareDataForSubmit = (data) => {
        const rows = _get(data, 'invoice_statuses', []) || [];

        return {
            event: convertToInt(this.props.formParams.optionsVariables.event),
            invoice_statuses: rows.map((row, index) => {
                const isReportable = _get(this.state, `reportableByIndex.${index}`, false),
                    item = {
                        distribution: convertToInt(row.distribution),
                        invoice_status: row.invoice_status ? convertToInt(row.invoice_status) : null,
                        auto_update: isReportable ? Boolean(row.auto_update) : true,
                        inherit_on_bookings: isReportable ? Boolean(row.inherit_on_bookings) : true,
                    };

                if (row.id) {
                    item.id = convertToInt(row.id);
                }

                return item;
            }),
        };
    };
}

export default withRouter(DistributionInvoiceStatusForm);
